import type { StagedReplacement } from '~/pages/backoffice/admin/modules/replacement-impact'
import { useMutation } from '@apollo/client/react'
import { useState } from 'react'
import { GQL_APPLY_MODULE_REPLACEMENT, GQL_CANCEL_MODULE_REPLACEMENT } from '~/gql/scorm-modules/scorm-modules'
import { useUploadWithProgress } from '~/hooks/use-upload-with-progress'
import { moduleUploadError } from '~/pages/backoffice/admin/modules/module-upload-error'
import { stagedReplacementSchema } from '~/pages/backoffice/admin/modules/replacement-impact'
import { useDescribeModuleUploadError } from '~/pages/backoffice/admin/modules/use-describe-module-upload-error'

/**
 * Le remplacement du contenu d'un module (#311), en deux temps : `stage`
 * envoie la nouvelle version — avec sa progression (#310) — et rend son
 * impact, sans rien changer à ce que voient les apprenants ; `apply` bascule,
 * `discard` jette le dépôt.
 */
export function useModuleReplacement(moduleId: string) {
  const { state, upload, cancel } = useUploadWithProgress()
  const describe = useDescribeModuleUploadError()
  const [staged, setStaged] = useState<StagedReplacement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [applying, setApplying] = useState(false)
  const [applyReplacement] = useMutation(GQL_APPLY_MODULE_REPLACEMENT)
  const [cancelReplacement] = useMutation(GQL_CANCEL_MODULE_REPLACEMENT)

  const stage = async (file: File) => {
    setError(null)
    const form = new FormData()
    form.append('file', file)
    const outcome = await upload(`/modules/${moduleId}/replacement`, form)
    if (outcome.kind === 'cancelled') return
    const cause = moduleUploadError(outcome)
    if (cause) {
      setError(describe(cause))
      return
    }
    const parsed = outcome.kind === 'response' ? stagedReplacementSchema.safeParse(outcome.response.body) : null
    if (parsed?.success) setStaged(parsed.data)
    else setError(describe({ kind: 'unknown' }))
  }

  const apply = async () => {
    if (!staged) return
    setApplying(true)
    try {
      await applyReplacement({ variables: { revisionId: staged.revisionId } })
    } finally {
      setApplying(false)
    }
  }

  /** Jette le dépôt en attente, s'il y en a un ; le cron le ferait sinon au bout d'un jour. */
  const discard = async () => {
    if (!staged) return
    setStaged(null)
    await cancelReplacement({ variables: { revisionId: staged.revisionId } }).catch(() => {})
  }

  return { staged, error, state, cancelUpload: cancel, stage, apply, discard, busy: state.phase !== 'idle' || applying }
}
